import type { NoteColor, NoteDto, TaskCommandType, TaskListItemDto } from '@ovo/shared'
import { NOTE_COLORS } from '@ovo/shared'
import type { ChangeEvent, DragEvent, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { errorMessage } from '../api/client.ts'
import {
  useCreateNote,
  useDeleteNote,
  useDeletePhoto,
  useMe,
  useMyShifts,
  useNotes,
  useReorderNotes,
  useShiftToggle,
  useTasks,
  useUpdateNote,
  useUploadPhoto,
} from '../api/hooks.ts'
import { daysBetween, formatMoment, formatTime, sameDay } from '../lib/dates.ts'
import { useNow } from '../lib/hooks.ts'
import { compressPhoto } from '../lib/photo.ts'
import { pl, TASK_FORMS } from '../lib/plural.ts'
import { isOpen, shortName, statusLabel } from '../lib/status.ts'
import { Avatar } from '../ui/Avatar.tsx'
import { Button } from '../ui/Button.tsx'
import { DeadlineLabel } from '../ui/DeadlineLabel.tsx'
import { ErrorState } from '../ui/EmptyState.tsx'
import { Textarea } from '../ui/Input.tsx'
import { IconArrowRight, IconCheck, IconEdit, IconPlus, IconTrash, IconUser } from '../ui/icons.tsx'
import { PriorityMark } from '../ui/PriorityMark.tsx'
import { Screen, ScreenHeader } from '../ui/ScreenHeader.tsx'
import { ConfirmDialog } from '../ui/Sheet.tsx'
import { Skeleton, SkeletonBlock } from '../ui/Skeleton.tsx'
import { snackbar } from '../ui/Snackbar.tsx'
import { StatusGlyph } from '../ui/StatusBadge.tsx'
import { useTaskActions } from '../ui/TaskActions.tsx'

// Главная CRM: мини-профиль, сводка дня, канбан-доска своих задач и личные заметки-стикеры.

/** Перетаскивание работает мышью; на телефоне — нажатие на значок статуса. */
const CAN_DRAG = typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches
const DONE_DAYS = 7

function todayLabel(now: Date): string {
  const s = new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }).format(now)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function hoursText(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h} ч ${String(m).padStart(2, '0')} мин` : `${m} мин`
}

// ───────── Мини-профиль ─────────

function ProfileCard() {
  const me = useMe()
  const upload = useUploadPhoto()
  const remove = useDeletePhoto()
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const pick = (): void => fileRef.current?.click()
  const onFile = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setBusy(true)
    try {
      await upload.mutateAsync(await compressPhoto(file))
    } catch (err) {
      // ошибки сервера уже показал хук; здесь — ошибки чтения и сжатия файла
      if (!upload.isError) snackbar.error(err instanceof Error ? err.message : 'Не удалось загрузить фото')
    } finally {
      setBusy(false)
    }
  }
  if (!me.data) {
    return (
      <div className="home-card home-profile">
        <Skeleton width={64} height={64} radius={20} />
        <SkeletonBlock lines={2} />
      </div>
    )
  }
  const m = me.data
  return (
    <div className="home-card home-profile">
      <button
        type="button"
        className={`home-profile__photo${busy ? ' is-busy' : ''}`}
        onClick={pick}
        disabled={busy}
        aria-label={m.photoVersion ? 'Сменить фото' : 'Добавить фото'}
        title={m.photoVersion ? 'Сменить фото' : 'Добавить фото'}
      >
        <Avatar name={m.displayName} id={m.id} size={72} photo={m.photoVersion} />
        <span className="home-profile__photo-badge" aria-hidden="true">
          {busy ? <span className="spinner" /> : <IconEdit size={14} strokeWidth={2.2} />}
        </span>
      </button>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => void onFile(e)} />
      <div className="home-profile__main">
        <div className="home-profile__name">{m.displayName}</div>
        <div className="home-profile__position">{m.position || 'Сотрудник OVO'}</div>
        <div className="home-profile__tags">
          {m.department && <span className="tag">{m.department.name}</span>}
          {m.isManager && <span className="tag">Руководитель</span>}
          {m.isAdmin && <span className="tag">Администратор</span>}
        </div>
        <div className="home-profile__photo-actions">
          <button type="button" className="link" onClick={pick} disabled={busy}>
            {m.photoVersion ? 'Сменить фото' : 'Добавить фото'}
          </button>
          {m.photoVersion ? (
            <button
              type="button"
              className="link"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              Убрать
            </button>
          ) : null}
          <span className="hint">видят вы и ваши руководители</span>
        </div>
      </div>
      <Link to="/settings" className="btn btn--ghost btn--sm home-profile__settings">
        Настройки
      </Link>
    </div>
  )
}

// ───────── Сводка дня ─────────

function Stat({ value, label, to, attn }: { value: number; label: string; to: string; attn?: boolean }) {
  return (
    <Link to={to} className={`home-stat${attn && value > 0 ? ' home-stat--attn' : ''}`}>
      <span className="home-stat__value">{value}</span>
      <span className="home-stat__label">{label}</span>
    </Link>
  )
}

function WorkDay({ openToday }: { openToday: number }) {
  const shifts = useMyShifts()
  const toggle = useShiftToggle()
  const now = useNow(30_000)
  const [confirmEnd, setConfirmEnd] = useState(false)
  if (shifts.isPending) return <Skeleton height={40} />
  if (shifts.isError) return null
  const s = shifts.data
  const today = s.byDay.find((d) => sameDay(new Date(`${d.day}T12:00:00`), now))
  let text: string
  if (s.away) text = `Вы отошли до ${formatTime(new Date(s.away.until))}`
  else if (s.openSince) {
    const worked = today?.workedMinutes ?? Math.round((now.getTime() - Date.parse(s.openSince)) / 60_000)
    text = `Рабочий день идёт с ${formatTime(new Date(s.openSince))} · ${hoursText(Math.max(0, worked))}`
  } else if (today && today.workedMinutes > 0) text = `Сегодня отработано ${hoursText(today.workedMinutes)}`
  else text = 'Рабочий день ещё не начат'
  return (
    <div className="home-workday">
      <span className={`home-workday__dot${s.openSince ? (s.away ? ' is-away' : ' is-on') : ''}`} />
      <span className="grow">{text}</span>
      {s.openSince ? (
        <Button size="sm" loading={toggle.isPending} onClick={() => setConfirmEnd(true)}>
          Завершить день
        </Button>
      ) : (
        <Button size="sm" variant="primary" loading={toggle.isPending} onClick={() => toggle.mutate('start')}>
          Начать день
        </Button>
      )}
      <ConfirmDialog
        open={confirmEnd}
        title="Завершить рабочий день?"
        text={
          openToday > 0
            ? `На сегодня ещё ${pl(openToday, TASK_FORMS)} без отметки «Готово». Можно обновить статус завтра — или сейчас, если успели.`
            : 'Руководитель получит тихое уведомление. Хорошего вечера!'
        }
        confirmText="Завершить"
        onClose={() => setConfirmEnd(false)}
        onConfirm={() => {
          setConfirmEnd(false)
          toggle.mutate('end')
        }}
      />
    </div>
  )
}

function DayCard({ open, doneToday }: { open: TaskListItemDto[]; doneToday: number }) {
  const me = useMe()
  const now = useNow()
  const review = useTasks({ scope: 'created', status: 'submitted' }, Boolean(me.data))
  const mine = open.filter((t) => t.status !== 'submitted')
  const overdue = mine.filter((t) => t.isOverdue).length
  const today = mine.filter(
    (t) => t.dueAt && !t.isOverdue && daysBetween(now, new Date(t.dueAt)) === 0 && t.status !== 'new',
  ).length
  const toAccept = open.filter((t) => t.status === 'new').length
  const toCheck = review.data ?? []
  return (
    <div className="home-card home-day">
      <div className="home-day__date">{todayLabel(now)}</div>
      {me.data?.shiftsEnabled && <WorkDay openToday={today + overdue} />}
      <div className="home-stats">
        <Stat value={today} label="на сегодня" to="/" />
        <Stat value={overdue} label="срок прошёл" to="/" attn />
        <Stat value={toAccept} label="нужно принять" to="/" attn />
        <Stat value={toCheck.length} label="ждут вашей проверки" to="/outbox?f=review" attn />
        <Stat value={doneToday} label="сделано сегодня" to="/" />
      </div>
    </div>
  )
}

// ───────── Канбан ─────────

type Col = 'new' | 'work' | 'issue' | 'review' | 'done'

const COLUMNS: Array<{ key: Col; title: string; hint: string }> = [
  { key: 'new', title: 'Новые', hint: 'Нажмите «Принять» или перетащите в «В работе»' },
  { key: 'work', title: 'В работе', hint: 'Задач в работе нет' },
  { key: 'issue', title: 'Вопрос / блокер', hint: 'Перетащите сюда, если что-то мешает' },
  { key: 'review', title: 'На проверке', hint: 'Перетащите сюда готовую задачу' },
  { key: 'done', title: `Готово за ${DONE_DAYS} дней`, hint: 'Здесь появятся сделанные задачи' },
]

function columnOf(t: TaskListItemDto): Col {
  if (t.status === 'new') return 'new'
  if (t.status === 'blocked' || t.pendingRequest === 'question') return 'issue'
  if (t.status === 'submitted') return 'review'
  if (!isOpen(t.status)) return 'done'
  return 'work'
}

/** Какое действие означает перенос карточки в колонку. null — так перенести нельзя. */
function dropCommand(t: TaskListItemDto, to: Col, meId: number): TaskCommandType | null {
  const from = columnOf(t)
  if (from === to) return null
  const doer = t.assignee.id === meId || t.coAssignees.some((c) => c.id === meId)
  const creator = t.creator.id === meId
  switch (to) {
    case 'work':
      if (doer && t.status === 'new') return 'accept'
      if (doer && t.status === 'blocked') return 'unblock'
      if (creator && t.status === 'submitted') return 'return'
      if (creator && !isOpen(t.status)) return 'reopen'
      return null
    case 'issue':
      if (doer && t.status === 'new') return 'ask'
      if (doer && (t.status === 'accepted' || t.status === 'in_progress')) return 'block'
      return null
    case 'review':
      return doer && (t.status === 'accepted' || t.status === 'in_progress') ? 'submit' : null
    case 'done':
      if (creator && t.status === 'submitted') return 'verify'
      if (doer && creator && (t.status === 'accepted' || t.status === 'in_progress')) return 'submit'
      return null
    default:
      return null
  }
}

function KanbanCard({
  task,
  meId,
  now,
  onGlyph,
  onDragStart,
  onDragEnd,
  pending,
}: {
  task: TaskListItemDto
  meId: number
  now: Date
  onGlyph: (t: TaskListItemDto) => void
  onDragStart: (t: TaskListItemDto) => void
  onDragEnd: () => void
  pending: boolean
}) {
  const closed = !isOpen(task.status)
  const self = task.creator.id === task.assignee.id
  return (
    <article
      className={`kanban-card${pending ? ' is-pending' : ''}${closed ? ' is-closed' : ''}`}
      draggable={CAN_DRAG && !pending}
      onDragStart={(e: DragEvent) => {
        e.dataTransfer.setData('text/plain', String(task.id))
        e.dataTransfer.effectAllowed = 'move'
        onDragStart(task)
      }}
      onDragEnd={onDragEnd}
    >
      <div className="kanban-card__top">
        <button
          type="button"
          className="kanban-card__glyph"
          aria-label={`${statusLabel(task.status)}. Изменить статус задачи «${task.title}»`}
          onClick={() => onGlyph(task)}
        >
          <StatusGlyph status={task.status} size={20} />
        </button>
        <Link to={`/tasks/${task.id}`} className="kanban-card__title" draggable={false}>
          {task.title}
        </Link>
        <PriorityMark priority={task.priority} />
      </div>
      <div className="kanban-card__meta">
        {!closed && task.status !== 'submitted' && (
          <DeadlineLabel
            dueAt={task.dueAt}
            dueKind={task.dueKind}
            status={task.status}
            isOverdue={task.isOverdue}
            now={now}
          />
        )}
        {self ? (
          <span className="tag">🙋 себе</span>
        ) : task.creator.id !== meId ? (
          <span className="kanban-card__person" title="Постановщик">
            <IconUser size={13} strokeWidth={2} />
            {shortName(task.creator.displayName)}
          </span>
        ) : (
          <span className="kanban-card__person" title="Исполнитель">
            → {shortName(task.assignee.displayName)}
          </span>
        )}
        {task.checklist && (
          <span className="tag" title="Пункты">
            ☑ {task.checklist.done}/{task.checklist.total}
          </span>
        )}
        {task.pendingRequest === 'question' && <span className="tag">Вопрос</span>}
        {task.project && <span className="tag">{task.project.name}</span>}
      </div>
    </article>
  )
}

function Kanban({ open, done, meId }: { open: TaskListItemDto[]; done: TaskListItemDto[]; meId: number }) {
  const actions = useTaskActions(meId)
  const now = useNow()
  const [drag, setDrag] = useState<TaskListItemDto | null>(null)
  const [over, setOver] = useState<Col | null>(null)

  const byCol = useMemo(() => {
    const map = new Map<Col, TaskListItemDto[]>(COLUMNS.map((c) => [c.key, []]))
    for (const t of [...open, ...done]) map.get(columnOf(t))?.push(t)
    const due = (t: TaskListItemDto) => (t.dueAt ? Date.parse(t.dueAt) : Number.POSITIVE_INFINITY)
    for (const [key, list] of map) {
      if (key === 'done') list.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
      else list.sort((a, b) => due(a) - due(b) || b.id - a.id)
    }
    return map
  }, [open, done])

  const drop = (col: Col): void => {
    const t = drag
    setDrag(null)
    setOver(null)
    if (!t) return
    const cmd = dropCommand(t, col, meId)
    if (!cmd) {
      if (columnOf(t) !== col) {
        snackbar.show({ text: 'Так перенести нельзя — откройте задачу, там все действия' })
      }
      return
    }
    actions.run(t, cmd)
  }

  return (
    <>
      <div className="kanban">
        {COLUMNS.map((c) => {
          const list = byCol.get(c.key) ?? []
          const target = drag ? dropCommand(drag, c.key, meId) !== null : false
          return (
            <section
              key={c.key}
              className={`kanban-col${target ? ' is-target' : ''}${over === c.key && target ? ' is-over' : ''}`}
              aria-label={c.title}
              onDragOver={(e) => {
                if (!target) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                if (over !== c.key) setOver(c.key)
              }}
              onDragLeave={() => over === c.key && setOver(null)}
              onDrop={(e) => {
                e.preventDefault()
                drop(c.key)
              }}
            >
              <header className="kanban-col__head">
                <span>{c.title}</span>
                <span className="kanban-col__count">{list.length}</span>
              </header>
              <div className="kanban-col__list">
                {list.map((t) => (
                  <KanbanCard
                    key={t.id}
                    task={t}
                    meId={meId}
                    now={now}
                    pending={actions.pendingTaskId === t.id}
                    onGlyph={(task) => actions.openMenu(task)}
                    onDragStart={setDrag}
                    onDragEnd={() => {
                      setDrag(null)
                      setOver(null)
                    }}
                  />
                ))}
                {list.length === 0 && <div className="kanban-col__empty">{c.hint}</div>}
              </div>
            </section>
          )
        })}
      </div>
      {actions.element}
    </>
  )
}

// ───────── Заметки-стикеры ─────────

const COLOR_LABELS: Record<NoteColor, string> = {
  yellow: 'Жёлтый',
  green: 'Зелёный',
  blue: 'Голубой',
  pink: 'Розовый',
  purple: 'Сиреневый',
  gray: 'Серый',
}

function ColorDots({ value, onChange }: { value: NoteColor; onChange: (c: NoteColor) => void }) {
  return (
    <fieldset className="note-colors">
      <legend className="visually-hidden">Цвет заметки</legend>
      {NOTE_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          aria-pressed={value === c}
          aria-label={COLOR_LABELS[c]}
          className={`note-colors__dot note--${c}${value === c ? ' is-on' : ''}`}
          onClick={() => onChange(c)}
        />
      ))}
    </fieldset>
  )
}

function NoteEditor({
  initialText = '',
  initialColor = 'yellow',
  saving,
  onSave,
  onCancel,
}: {
  initialText?: string
  initialColor?: NoteColor
  saving: boolean
  onSave: (text: string, color: NoteColor) => void
  onCancel: () => void
}) {
  const [text, setText] = useState(initialText)
  const [color, setColor] = useState<NoteColor>(initialColor)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  // Заметку только что открыли для ввода — сразу ставим курсор в конец текста
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
  }, [])
  const ok = text.trim().length > 0
  const save = (): void => {
    if (ok && !saving) onSave(text.trim(), color)
  }
  const onKeyDown = (e: ReactKeyboardEvent): void => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      save()
    }
    if (e.key === 'Escape') onCancel()
  }
  return (
    <div className={`note note--${color} note--editing`}>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Что не забыть?"
        aria-label="Текст заметки"
        maxLength={1000}
        rows={4}
        autoGrow
        ref={inputRef}
      />
      <ColorDots value={color} onChange={setColor} />
      <div className="note__actions">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Отмена
        </Button>
        <Button size="sm" variant="primary" disabled={!ok} loading={saving} onClick={save}>
          Сохранить
        </Button>
      </div>
    </div>
  )
}

function NoteCard({
  note,
  onDragStart,
  onDragEnd,
  onDropOn,
  dragging,
}: {
  note: NoteDto
  onDragStart: (id: number) => void
  onDragEnd: () => void
  onDropOn: (id: number) => void
  dragging: boolean
}) {
  const navigate = useNavigate()
  const update = useUpdateNote()
  const remove = useDeleteNote()
  const create = useCreateNote()
  const [editing, setEditing] = useState(false)
  const now = useNow()

  if (editing) {
    return (
      <NoteEditor
        initialText={note.text}
        initialColor={note.color}
        saving={update.isPending}
        onCancel={() => setEditing(false)}
        onSave={(text, color) =>
          update.mutate({ id: note.id, text, color }, { onSuccess: () => setEditing(false) })
        }
      />
    )
  }

  const onDelete = (): void => {
    remove.mutate(note.id)
    snackbar.show({
      text: 'Заметка удалена',
      action: { label: 'Вернуть', onClick: () => create.mutate({ text: note.text, color: note.color }) },
    })
  }

  return (
    <article
      className={`note note--${note.color}${dragging ? ' is-dragging' : ''}`}
      draggable={CAN_DRAG}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', `note:${note.id}`)
        e.dataTransfer.effectAllowed = 'move'
        onDragStart(note.id)
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragEnd={onDragEnd}
      onDrop={(e) => {
        e.preventDefault()
        onDropOn(note.id)
      }}
    >
      <button type="button" className="note__text" onClick={() => setEditing(true)} title="Изменить">
        {note.text}
      </button>
      {note.task && (
        <Link to={`/tasks/${note.task.id}`} className="note__task">
          <IconCheck size={14} strokeWidth={2.2} /> Задача #{note.task.id} · {statusLabel(note.task.status)}
        </Link>
      )}
      <div className="note__foot">
        <span className="note__date">{formatMoment(note.updatedAt, now)}</span>
        <span className="note__tools">
          {!note.task && (
            <button
              type="button"
              className="note__tool"
              aria-label="Сделать задачей"
              title="Сделать задачей"
              onClick={() => navigate(`/tasks/new?note=${note.id}`)}
            >
              <IconArrowRight size={16} />
            </button>
          )}
          <button
            type="button"
            className="note__tool"
            aria-label="Изменить заметку"
            onClick={() => setEditing(true)}
          >
            <IconEdit size={16} />
          </button>
          <button type="button" className="note__tool" aria-label="Удалить заметку" onClick={onDelete}>
            <IconTrash size={16} />
          </button>
        </span>
      </div>
    </article>
  )
}

function NotesBoard() {
  const notes = useNotes()
  const create = useCreateNote()
  const reorder = useReorderNotes()
  const [adding, setAdding] = useState(false)
  const [dragId, setDragId] = useState<number | null>(null)

  const dropOn = (targetId: number): void => {
    const list = notes.data ?? []
    const from = dragId
    setDragId(null)
    if (from === null || from === targetId) return
    const ids = list.map((n) => n.id).filter((id) => id !== from)
    ids.splice(ids.indexOf(targetId), 0, from)
    reorder.mutate(ids)
  }

  return (
    <section className="home-block" aria-labelledby="home-notes">
      <div className="home-block__head">
        <h2 id="home-notes" className="home-block__title">
          Заметки
        </h2>
        <span className="hint grow">Видите только вы</span>
        {!adding && (
          <Button size="sm" icon={<IconPlus size={16} strokeWidth={2.2} />} onClick={() => setAdding(true)}>
            Заметка
          </Button>
        )}
      </div>
      {notes.isPending ? (
        <SkeletonBlock lines={3} />
      ) : notes.isError ? (
        <ErrorState text={errorMessage(notes.error)} onRetry={() => void notes.refetch()} />
      ) : (
        <div className="notes">
          {adding && (
            <NoteEditor
              saving={create.isPending}
              onCancel={() => setAdding(false)}
              onSave={(text, color) => create.mutate({ text, color }, { onSuccess: () => setAdding(false) })}
            />
          )}
          {notes.data.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              dragging={dragId === n.id}
              onDragStart={setDragId}
              onDragEnd={() => setDragId(null)}
              onDropOn={dropOn}
            />
          ))}
          {notes.data.length === 0 && !adding && (
            <button type="button" className="note note--empty" onClick={() => setAdding(true)}>
              Записывайте сюда мысли и дела на потом. Любую заметку можно превратить в задачу.
            </button>
          )}
        </div>
      )}
    </section>
  )
}

// ───────── Экран ─────────

export function HomeScreen() {
  const me = useMe()
  const meId = me.data?.id ?? 0
  const open = useTasks({ scope: 'mine' }, Boolean(me.data))
  const closed = useTasks({ scope: 'mine', state: 'closed' }, Boolean(me.data))
  const now = useNow()

  const done = useMemo(
    () =>
      (closed.data ?? []).filter(
        (t) => t.status === 'done' && now.getTime() - Date.parse(t.updatedAt) < DONE_DAYS * 86_400_000,
      ),
    [closed.data, now],
  )
  const doneToday = done.filter((t) => sameDay(new Date(t.updatedAt), now)).length

  return (
    <Screen label="Главная" className="screen--home">
      <ScreenHeader title="Главная" />
      <div className="home-top">
        <ProfileCard />
        <DayCard open={open.data ?? []} doneToday={doneToday} />
      </div>

      <section className="home-block" aria-labelledby="home-board">
        <div className="home-block__head">
          <h2 id="home-board" className="home-block__title">
            Мои задачи
          </h2>
          <span className="hint grow">
            {CAN_DRAG ? 'Перетаскивайте карточки между колонками' : 'Статус меняется нажатием на значок'}
          </span>
          <Link to="/tasks/new" className="btn btn--sm btn--secondary">
            <IconPlus size={16} strokeWidth={2.2} /> Задача
          </Link>
        </div>
        {open.isPending || me.isPending ? (
          <SkeletonBlock lines={4} />
        ) : open.isError ? (
          <ErrorState text={errorMessage(open.error)} onRetry={() => void open.refetch()} />
        ) : (
          <Kanban open={open.data} done={done} meId={meId} />
        )}
      </section>

      <NotesBoard />
    </Screen>
  )
}
